import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Newspaper,
  RefreshCw,
  Loader2,
  ExternalLink,
  Sparkles,
  Copy,
  X,
  Calendar,
  Users,
  Stethoscope,
  Heart,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TA_LABELS = {
  oncology: 'Oncology',
  cardiology: 'Cardiology',
  neurology: 'Neurology',
  autoimmune: 'Autoimmune',
  dermatology: 'Dermatology',
  pulmonary: 'Pulmonary',
  endocrinology: 'Endocrinology',
  gastroenterology: 'Gastroenterology',
};

const TA_ORDER = ['oncology', 'cardiology', 'neurology', 'autoimmune', 'dermatology', 'pulmonary', 'endocrinology', 'gastroenterology'];

const AUDIENCES = [
  { key: 'patients', label: 'Patients & Caregivers', icon: Heart, color: 'from-rose-500 to-pink-600' },
  { key: 'physicians', label: 'Physicians / Sites', icon: Stethoscope, color: 'from-emerald-500 to-teal-600' },
  { key: 'sponsors', label: 'Sponsors / CROs', icon: Users, color: 'from-indigo-500 to-blue-600' },
];

const FORMATS = [
  { key: 'linkedin', label: 'LinkedIn Post' },
  { key: 'blog', label: 'Blog Article' },
  { key: 'newsletter', label: 'Newsletter' },
  { key: 'reel', label: 'Meta Reel Script' },
  { key: 'podcast', label: 'Podcast Outline' },
];

const NewsStudio = ({ onBack }) => {
  const [ta, setTa] = useState('oncology');
  const [items, setItems] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [audience, setAudience] = useState('patients');
  const [format, setFormat] = useState('linkedin');
  const [isGenerating, setIsGenerating] = useState(false);
  const [output, setOutput] = useState(null);

  const fetchNews = useCallback(async (refresh = false) => {
    setIsFetching(true);
    setSelected(null);
    setOutput(null);
    try {
      const r = await axios.get(`${API}/news/fetch`, {
        params: { ta, days: 7, refresh },
        timeout: 45000,
      });
      setItems(r.data.items || []);
      setFetchedAt(r.data.fetched_at);
      if ((r.data.items || []).length === 0) {
        toast.info('No fresh news found in the last 7 days — try refreshing or another TA.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch news');
    } finally {
      setIsFetching(false);
    }
  }, [ta]);

  useEffect(() => { fetchNews(false); }, [fetchNews]);

  const generate = async () => {
    if (!selected) return;
    setIsGenerating(true);
    setOutput(null);
    try {
      const r = await axios.post(`${API}/news/generate`, {
        news: selected,
        audience,
        format,
      }, { timeout: 120000 });
      setOutput(r.data);
      toast.success('Content generated and saved to My Content');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="news-studio">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="news-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#0070FF] to-[#0050CC] flex items-center justify-center">
                <Newspaper className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg leading-tight">News Studio</h1>
                <p className="text-xs text-gray-500">Fresh medical news → ready-to-post content</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => fetchNews(true)}
            disabled={isFetching}
            data-testid="refresh-news-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh news
          </button>
        </div>

        {/* TA chips */}
        <div className="max-w-7xl mx-auto px-4 pb-3 flex gap-2 flex-wrap">
          {TA_ORDER.map((k) => (
            <button
              key={k}
              onClick={() => setTa(k)}
              data-testid={`ta-chip-${k}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                ta === k
                  ? 'bg-[#0070FF] text-white border-[#0070FF]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-[#0070FF]'
              }`}
            >
              {TA_LABELS[k]}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h2 className="font-heading font-bold text-xl">Latest {TA_LABELS[ta]} News</h2>
            <p className="text-xs text-gray-500">
              Blended from PubMed + Google News · last 7 days
              {fetchedAt && ` · fetched ${new Date(fetchedAt).toLocaleTimeString()}`}
            </p>
          </div>
        </div>

        {isFetching ? (
          <div className="py-20 flex items-center justify-center text-gray-500 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Fetching fresh headlines…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center text-sm text-gray-500">
            No news found. Try refreshing or picking another therapeutic area.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it, i) => (
              <motion.button
                key={it.id}
                data-testid={`news-card-${i}`}
                onClick={() => { setSelected(it); setOutput(null); }}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="text-left bg-white rounded-xl border border-gray-200 hover:border-[#0070FF] hover:shadow-md p-4 transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${
                    it.source === 'pubmed' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'
                  }`}>
                    {it.source === 'pubmed' ? 'PubMed' : 'News'}
                  </span>
                  <span className="text-[10px] text-gray-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {it.published_at ? new Date(it.published_at).toLocaleDateString() : '—'}
                  </span>
                </div>
                <h3 className="font-medium text-sm leading-snug mb-2 line-clamp-3">{it.title}</h3>
                {it.summary && (
                  <p className="text-xs text-gray-500 line-clamp-2 mb-2">{it.summary}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 truncate max-w-[70%]">{it.outlet || ''}</span>
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-[#0070FF] inline-flex items-center gap-1 hover:underline"
                  >
                    Source <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {/* Detail / generation drawer */}
      <AnimatePresence>
        {selected && (
          <motion.div
            className="fixed inset-0 bg-black/40 z-50 flex justify-end"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => { setSelected(null); setOutput(null); }}
          >
            <motion.aside
              className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl"
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="news-drawer"
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="font-heading font-bold text-lg">Generate content</h3>
                <button
                  onClick={() => { setSelected(null); setOutput(null); }}
                  data-testid="close-drawer-btn"
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-6">
                {/* News summary */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-2">
                    <span className={`px-2 py-0.5 rounded ${selected.source === 'pubmed' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                      {selected.source === 'pubmed' ? 'PubMed' : 'News'}
                    </span>
                    <span>{selected.outlet}</span>
                    {selected.published_at && <span>· {new Date(selected.published_at).toLocaleDateString()}</span>}
                  </div>
                  <p className="font-medium text-sm leading-snug mb-2">{selected.title}</p>
                  {selected.summary && <p className="text-xs text-gray-600 mb-2">{selected.summary}</p>}
                  <a href={selected.url} target="_blank" rel="noreferrer" className="text-xs text-[#0070FF] inline-flex items-center gap-1 hover:underline">
                    Read source <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                {/* Audience */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Audience</label>
                  <div className="grid grid-cols-3 gap-2">
                    {AUDIENCES.map((a) => {
                      const Icon = a.icon;
                      const active = audience === a.key;
                      return (
                        <button
                          key={a.key}
                          onClick={() => setAudience(a.key)}
                          data-testid={`audience-${a.key}`}
                          className={`p-3 rounded-lg text-left border transition-all ${
                            active
                              ? `bg-gradient-to-br ${a.color} text-white border-transparent shadow-sm`
                              : 'bg-white border-gray-200 hover:border-gray-300 text-gray-700'
                          }`}
                        >
                          <Icon className="w-4 h-4 mb-1" />
                          <div className="text-xs font-medium leading-tight">{a.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Format */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Format</label>
                  <div className="flex gap-2 flex-wrap">
                    {FORMATS.map((f) => (
                      <button
                        key={f.key}
                        onClick={() => setFormat(f.key)}
                        data-testid={`format-${f.key}`}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                          format === f.key
                            ? 'bg-[#0070FF] text-white border-[#0070FF]'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-[#0070FF]'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate */}
                <button
                  onClick={generate}
                  disabled={isGenerating}
                  data-testid="generate-content-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0070FF] text-white font-semibold hover:bg-[#0050CC] transition-colors disabled:opacity-60"
                >
                  {isGenerating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Generate content</>
                  )}
                </button>

                {/* Output */}
                {output && (
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="generated-content">
                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                      <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-gray-500">
                          {FORMATS.find((f) => f.key === output.format)?.label} · {AUDIENCES.find((a) => a.key === output.audience)?.label}
                        </p>
                      </div>
                      <button
                        onClick={() => copy(output.content)}
                        data-testid="copy-output-btn"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-white"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy
                      </button>
                    </div>
                    <div className="p-4 whitespace-pre-wrap text-sm leading-relaxed text-gray-800 font-mono">
                      {output.content}
                    </div>
                  </div>
                )}
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsStudio;
